import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, CardTitle, Spinner, Alert } from "reactstrap";
import { useStaffStore } from "../../stores/Staffs";
import { tokenHelper } from "../../stores/helpers";


const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitationData, setInvitationData] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Get store methods and state
  const {
    verifyInvitationToken,
    acceptInvitationToken,
    isLoading
  } = useStaffStore();

  useEffect(() => {
    verifyInvitation();
  }, [token]);

  const verifyInvitation = async () => {
    try {
      console.log("Verifying invitation...");
      setLocalError(null);
      const data = await verifyInvitationToken(token);
      setInvitationData(data);
    } catch (err) {
      console.error("Error verifying invitation:", err);
      setLocalError(
        err.response?.data?.message ||
        "Invalid or expired invitation. Please contact the person who invited you for a new invitation."
      );
    }
  };

  const handleAcceptInvitation = async () => {
    try {
      setAccepting(true);
      console.log("Accept invitation clicked, accepting invitation token...");

      // First accept the invitation token (mark as ACCEPTED)
      const response = await acceptInvitationToken(token);

      // Then redirect to registration page
      navigate(`/auth/register-from-invitation/${token}`);
    } catch (err) {
      console.error("Error accepting invitation:", err);
      setLocalError("Failed to accept invitation. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  const handleDeclineInvitation = () => {
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <Card>
                <CardBody className="text-center p-5">
                  <Spinner color="primary" className="mb-3" />
                  <h5>Verifying invitation...</h5>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (localError) {
    return (
      <div className="auth-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <Card>
                <CardHeader>
                  <CardTitle className="text-center text-danger">
                    Invalid Invitation
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Alert color="danger">
                    {localError}
                  </Alert>
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/login")}
                    >
                      Go to Login
                    </button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invitationData) {
    console.log("No invitation data, showing nothing");
    return null;
  }

  return (
    <div className="auth-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="h4 mb-0">
                  <i className="ri-user-add-line me-2"></i>
                  You're Invited!
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-4">
                  <div className="avatar-lg mx-auto mb-3">
                    <div className="avatar-title rounded-circle bg-success-subtle text-success">
                      <i className="ri-user-add-line fs-24"></i>
                    </div>
                  </div>
                  <h5 className="text-primary">
                    {invitationData.inviter.name || invitationData.inviter.email}
                    has invited you to join
                  </h5>
                  <h4 className="text-dark">{invitationData.site.name}</h4>
                </div>

                <div className="invitation-details mb-4">
                  <div className="row">
                    <div className="col-12 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="ri-building-line text-primary me-2"></i>
                        <div>
                          <strong>Site:</strong> {invitationData.site.name}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="d-flex align-items-start">
                        <i className="ri-space text-primary me-2 mt-1"></i>
                        <div>
                          <strong>Spaces you'll have access to:</strong>
                          <ul className="list-unstyled mt-1 mb-0">
                            {invitationData.space_ids.map((spaceId, index) => (
                              <li key={index} className="text-muted">
                                • Space ID: {spaceId}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="ri-user-line text-primary me-2"></i>
                        <div>
                          <strong>Invited by:</strong> {invitationData.inviter.name || invitationData.inviter.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info">
                  <i className="ri-information-line me-2"></i>
                  <strong>What's next?</strong> Click "Accept Invitation" to create your account
                  and get access to the selected spaces. You'll be able to manage orders and settings.
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-success btn-lg me-2"
                    onClick={handleAcceptInvitation}
                    disabled={accepting}
                  >
                    {accepting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line me-2"></i>
                        Accept Invitation
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-lg"
                    onClick={handleDeclineInvitation}
                    disabled={accepting}
                  >
                    <i className="ri-close-line me-2"></i>
                    Decline
                  </button>
                </div>

                {localError && (
                  <Alert color="danger" className="mt-3">
                    {localError}
                  </Alert>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
